﻿import { useParams, useNavigate } from 'react-router-dom'
import { useEffect, useState, useCallback } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { sileo } from 'sileo'
import { supabase } from '../lib/supabase'

// services
import ticketService from '../services/ticketService'
import { employeeService } from '../services/employeeService'
import { fileService } from '../services/fileService'
import { solutionService } from '../services/solutionService'
import generatePDF from '../utils/GeneratePDF'

// Hooks y Componentes
import { useTicket } from '../hooks/useTicket'
import useModal from '../hooks/useModal'
import Modal from '../components/Shared/Modal'
import FileList from '../components/File/FileList'
import CommentaryList from '../components/comment/CommentaryList'
import CommentaryAdd from '../components/comment/CommentaryAdd'
import ListSolution from '../components/Solution/ListSolution'
import AddSolution from '../components/Solution/AddSolution'
import TicketAISolution from '../components/Solution/TicketAISolution'
import AssignUserTicket from '../components/user/AssignUserTicket'
import ResponsibleCard from '../components/user/ResponsibleCard'
import TicketStatus from '../components/status/TicketStatus'
import TicketTime from '../components/Time/TicketTime'
import TicketChangeStatus from '../components/status/TicketChangeStatus'
import TicketAuthorCard from '../components/user/TicketAuthorCard'
import TagTicketList from '../components/Tag/TagTicketList'
import TagTicketAdd from '../components/Tag/TagTicketAdd'
import AuthAccessValidator from '../components/Auth/AuthAccessValidator'
import EditTicket from '../components/ticket/EditTicket'
import SlaCard from '../components/ticket/SlaCard'
import TicketTypeBadge from '../components/ticket/TicketTypeBadge'
import TicketClassification from '../components/ticket/TicketClassification'
import HistoryTimeline from '../components/ticket/HistoryTimeline'
import TicketQueueCard from '../components/Queue/TicketQueueCard'
import useTicketCatalogs from '../hooks/useTicketCatalogs'
import useQueueTransfer from '../hooks/useQueueTransfer'
import useHasAccess from '../hooks/useHasAccess'

const ViewTicket = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [isAssigning, setIsAssigning] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [historyVersion, setHistoryVersion] = useState(0)
  const [isAddingTag, setIsAddingTag] = useState(false)
  const [copied, setCopied] = useState(false)

  const {
    openModal,
    openLargeModal,
    isOpen,
    modalConfig,
    closeModal,
    isLargeModal,
  } = useModal()

  const [data, setData] = useState({
    files: [],
    commentaries: [],
    solutions: [],
    userCreator: null,
  })

  const {
    ticket,
    loading,
    running,
    status,
    employee,
    tags,
    formatTime,
    changeStatus,
    start,
    stop,
    toggleCompleted,
    RefreshTags,
    assignResponsible,
    time,
    patchTicket,
  } = useTicket(id)

  // Cola de atención: catálogo de colas y transferencia (sólo soporte).
  const { queues, queueById } = useTicketCatalogs()
  const canTransfer = useHasAccess('ticket_support')
  const handleTransferred = useCallback(
    (changes) => {
      patchTicket(changes)
      setHistoryVersion((v) => v + 1)
    },
    [patchTicket]
  )
  const { transfer, transferring } = useQueueTransfer(id, handleTransferred)

  const fetchTicketDetails = useCallback(async () => {
    try {
      const [solutionsRes, filesRes, commRes] = await Promise.all([
        // 🔹 SOLUCIONES
        supabase
          .schema('Tickets')
          .from('solution')
          .select(
            `
          id,
          title,
          description,
          ticketSolution!inner (
            ticketId
          )
        `
          )
          .eq('ticketSolution.ticketId', id),

        // 🔹 ARCHIVOS DEL TICKET (sin solución)
        supabase
          .schema('Tickets')
          .from('file')
          .select('*')
          .eq('ticketId', id)
          .is('solutionId', null),

        // 🔹 COMENTARIOS
        supabase
          .schema('Tickets')
          .from('comment')
          .select('*')
          .eq('ticketId', id)
          .order('createdAt', { ascending: true }),
      ])

      const solutions = solutionsRes.data || []

      // 🔹 ARCHIVOS DE SOLUCIONES — traídos por solutionId
      let solutionsWithFiles = solutions
      if (solutions.length > 0) {
        const solutionIds = solutions.map((s) => s.id)
        const { data: solutionFiles } = await supabase
          .schema('Tickets')
          .from('file')
          .select('*')
          .in('solutionId', solutionIds)

        // Adjuntar archivos a cada solución
        solutionsWithFiles = solutions.map((sol) => ({
          ...sol,
          files: (solutionFiles || []).filter((f) => f.solutionId === sol.id),
        }))
      }

      setData((prev) => ({
        ...prev,
        solutions: solutionsWithFiles,
        files: filesRes.data || [],
        commentaries: commRes.data || [],
      }))
    } catch (err) {
      console.error('Error:', err)
    }
  }, [id])

  useEffect(() => {
    fetchTicketDetails()
  }, [fetchTicketDetails])

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const handleAddTag = async (item) => {
    try {
      const { error } = await supabase
        .schema('Tickets')
        .from('ticketTag')
        .insert([
          {
            ticketId: ticket.id,
            tagId: item.id,
          },
        ])

      if (error) throw error

      RefreshTags()
      setIsAddingTag(false)
    } catch (error) {
      console.error(error)
    }
  }

  // Las notificaciones al creador (cambio a cerrado/finalizado y nuevos
  // comentarios) se generan automáticamente vía triggers de base de datos.

  const handleChangeStatus = async (statusId) => {
    try {
      changeStatus(statusId)
    } catch (error) {
      console.error(error)
    }
  }

  const CompleteTicket = () => {
    if (!ticket.completed) {
      stop()
    }
    toggleCompleted()
  }

  useEffect(() => {
    const fetchUserCreator = async () => {
      if (!ticket?.userId) return
      try {
        const { success, data, error } = await employeeService.getByUserId(
          ticket.userId
        )
        if (!success) {
          console.error('Error fetching employee by userId:', error)
          return
        }
        setData((prev) => ({
          ...prev,
          userCreator: data,
        }))
      } catch (error) {
        console.error('Error fetching employee by userId:', error)
      }
    }

    fetchUserCreator()
  }, [ticket?.userId])

  const handleAssign = (emp) => {
    assignResponsible(emp.id)
    setIsAssigning(false)
  }

  const handleAddedSolution = () => {
    try {
      closeModal()
      fetchTicketDetails()
    } catch (error) {
      console.error(error)
    }
  }

  const handleEditTicket = () => {
    try {
      openLargeModal(
        'Editar Ticket',
        <EditTicket
          ticket={ticket}
          onClosed={() => {
            closeModal()
            setHistoryVersion((v) => v + 1)
          }}
        />
      )
    } catch (error) {
      console.error(error)
    }
  }

  const handleDeleteTicket = async () => {
    const confirmed = window.confirm(
      `¿Seguro que deseas eliminar el ticket #${ticket.id}? Podrá restaurarse más adelante.`
    )
    if (!confirmed) return

    try {
      setIsDeleting(true)
      const { success, error } = await ticketService.softDelete(ticket.id)
      if (!success) throw new Error(error)

      sileo.success({ title: 'Ticket eliminado' })
      navigate('/')
    } catch (error) {
      console.error('Error al eliminar el ticket:', error)
      sileo.error({
        title: 'No se pudo eliminar',
        description: error.message,
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteSolution = async (solutionId) => {
    try {
      // 1️⃣ Borrar archivos asociados a la solución
      const { error: filesSolutionError } =
        await fileService.deleteBySolutionId(solutionId)

      if (filesSolutionError) throw filesSolutionError

      // 2️⃣ Borrar relación ticketSolution
      const { error: relError } = await solutionService.DeleteSolutionTicketId(
        solutionId,
        ticket.id
      )

      if (relError) throw relError

      // 3️⃣ Borrar la solución
      const { error: solDeleteError } =
        await solutionService.DeleteSolution(solutionId)
      if (solDeleteError) throw solDeleteError

      fetchTicketDetails()
    } catch (error) {
      console.error('Error al eliminar la solución:', error.message)
    }
  }

  const generateDownload0 = () => {
    generatePDF(ticket, {
      files: data.files,
      commentaries: data.commentaries,
      solutions: data.solutions,
      userCreator: data.userCreator,
    })
  }

  if (loading)
    return (
      <div className="p-5 text-center">
        <div className="spinner-border text-primary" />
      </div>
    )
  if (!ticket)
    return (
      <div className="p-5 text-center text-body-secondary">
        Ticket no encontrado
      </div>
    )
  const currentStatus = status?.find(
    (s) => String(s.id) === String(ticket.statusId)
  )

  return (
    <div className="container-fluid py-3 text-body vt-page">
      {/* MODALES */}
      <AnimatePresence>
        {isOpen && (
          <Modal
            isOpen={isOpen}
            onClose={closeModal}
            title={modalConfig.title}
            isLarge={isLargeModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              {modalConfig.component}
            </motion.div>
          </Modal>
        )}
      </AnimatePresence>

      {/* CABECERA */}
      <header className="vt-header">
        <div className="min-w-0 flex-grow-1">
          <div className="vt-meta">
            <TicketTypeBadge type={ticket.type} sm />
            <span className="vt-id">#{ticket.id}</span>
            <span className="vt-sep">·</span>
            <span>
              <i className="fa-regular fa-calendar me-1"></i>
              {new Date(ticket.createdAt).toLocaleDateString()}
            </span>
            {currentStatus && (
              <>
                <span className="vt-sep">·</span>
                <span className="vt-chip">{currentStatus.name}</span>
              </>
            )}
            {ticket.completed && (
              <span className="vt-chip is-done">
                <i className="fa-solid fa-check"></i>
                Completado
              </span>
            )}
            {running && (
              <span className="vt-running">
                <span className="vt-running-dot pulse-animation" />
                En curso
              </span>
            )}
          </div>
          <h1 className="vt-title">{ticket.title}</h1>
        </div>

        {/* Botones de acción */}
        <div className="vt-actions no-print">
          <button
            className="btn btn-sm vt-icon-btn"
            onClick={() => handleEditTicket()}
            title="Editar"
          >
            <i className="fa-solid fa-pencil"></i>
          </button>
          <button
            className="btn btn-sm vt-icon-btn"
            onClick={() => generateDownload0()}
            title="Imprimir ticket"
          >
            <i className="fa-solid fa-print"></i>
          </button>
          <button
            className={`btn btn-sm vt-icon-btn ${copied ? 'text-success' : ''}`}
            onClick={handleCopyUrl}
            title="Copiar enlace del ticket"
          >
            <i className={`fa-solid ${copied ? 'fa-check' : 'fa-link'}`}></i>
          </button>
          <AuthAccessValidator
            AccessNames={['ticket_support']}
            showError={false}
          >
            <span className="vt-actions-sep" />
            <button
              className="btn btn-sm vt-icon-btn is-danger"
              onClick={handleDeleteTicket}
              disabled={isDeleting}
              title="Eliminar ticket"
            >
              <i
                className={`fa-solid ${isDeleting ? 'fa-spinner fa-spin' : 'fa-trash'}`}
              ></i>
            </button>
          </AuthAccessValidator>
        </div>
      </header>

      <div className="row g-3">
        {/* COLUMNA PRINCIPAL */}
        <div
          id="principal"
          className="col-lg-8 col-xl-9 d-flex flex-column gap-3"
        >
          {/* Descripción + adjuntos */}
          <section className="vt-card">
            <div className="vt-section">
              <h6 className="section-subtitle mb-2">Descripción</h6>
              {ticket.description ? (
                <div className="vt-description">{ticket.description}</div>
              ) : (
                <p className="vt-empty">Sin descripción</p>
              )}
            </div>
            <div className="vt-section is-divided">
              <FileList files={data.files} openLargeModal={openLargeModal} />
            </div>
          </section>

          {/* Soluciones + IA */}
          <section className="vt-card">
            <div className="vt-section">
              <div className="vt-section-head">
                <h6 className="section-subtitle m-0">Soluciones</h6>
                {data.solutions.length === 0 && (
                  <span className="vt-empty">Sin soluciones registradas</span>
                )}

                <AuthAccessValidator
                  AccessNames={['ticket_support']}
                  showError={false}
                >
                  {!ticket.completed && (
                    <button
                      className="btn btn-sm vt-ghost-btn ms-auto no-print"
                      onClick={() =>
                        openModal(
                          'Agregar Solución',
                          <AddSolution
                            ticketId={id}
                            onAdded={handleAddedSolution}
                          />
                        )
                      }
                    >
                      <i className="fa-solid fa-plus"></i> Agregar
                    </button>
                  )}
                </AuthAccessValidator>
              </div>
              {data.solutions.length > 0 && (
                <div className="mt-2">
                  <ListSolution
                    items={data.solutions}
                    onDelete={handleDeleteSolution}
                  />
                </div>
              )}
            </div>
            <div className="vt-section is-divided vt-ai">
              <TicketAISolution
                ticketId={id}
                ticket={ticket}
                comentarios={data.commentaries}
              />
            </div>
          </section>

          {/* Conversación */}
          <section className="vt-card vt-thread">
            <div className="vt-section">
              <CommentaryList
                ticketId={ticket.id}
                commentaries={data.commentaries}
              />
            </div>
            <div className="vt-section is-divided vt-composer no-print">
              <CommentaryAdd
                ticketId={ticket.id}
                onAdded={(c) =>
                  setData((p) => ({
                    ...p,
                    commentaries: [...p.commentaries, c],
                  }))
                }
              />
            </div>
          </section>

          {/* Historial */}
          <section className="vt-card no-print">
            <div className="vt-section">
              <HistoryTimeline
                ticketId={ticket.id}
                watch={`${ticket.statusId}-${ticket.responsibleId}-${ticket.completed}-${ticket.priorityId}-${ticket.impact}-${ticket.urgency}-${historyVersion}`}
              />
            </div>
          </section>
        </div>

        {/* LATERAL DERECHO */}
        <aside className="col-lg-4 col-xl-3 no-print">
          <div className="vt-aside">
            <TicketChangeStatus
              IsCompleted={ticket.completed}
              toggleCompleted={CompleteTicket}
            />

            {/* Panel de propiedades */}
            <div className="vt-card vt-props">
              <TicketStatus
                status={status}
                ticket={ticket}
                changeStatus={handleChangeStatus}
              />

              <div className="vt-prop-block">
                <TicketQueueCard
                  queue={queueById[ticket.queueId] || null}
                  queues={queues}
                  canTransfer={canTransfer}
                  transferring={transferring}
                  onTransfer={transfer}
                />
              </div>

              <div className="vt-prop-block">
                <div className="vt-prop-head">
                  <span className="vt-prop-label">Responsable</span>
                  {employee && !isAssigning && (
                    <button
                      className="btn btn-sm btn-link p-0 text-decoration-none vt-link"
                      onClick={() => setIsAssigning(true)}
                    >
                      Cambiar
                    </button>
                  )}
                </div>

                {!employee || isAssigning ? (
                  <div className="vt-inset p-2">
                    <AuthAccessValidator
                      AccessNames={['ticket_support']}
                      showError={false}
                    >
                      <AssignUserTicket assign={handleAssign} />
                    </AuthAccessValidator>
                    {employee && (
                      <button
                        className="btn btn-sm btn-outline-secondary w-100 mt-2"
                        onClick={() => setIsAssigning(false)}
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                ) : (
                  <ResponsibleCard employee={employee} />
                )}
              </div>

              <div className="vt-prop">
                <span className="vt-prop-label">Solicitante</span>
                <TicketAuthorCard user={data.userCreator} />
              </div>

              <TicketClassification ticket={ticket} />

              {/* Etiquetas */}
              <div className="vt-prop-block">
                <div className="vt-prop-head">
                  <span className="vt-prop-label">Etiquetas</span>
                  <button
                    className="btn btn-sm vt-icon-btn is-sm"
                    onClick={() => setIsAddingTag(!isAddingTag)}
                    title="Gestionar etiquetas"
                  >
                    <i
                      className={`fa-solid ${isAddingTag ? 'fa-xmark' : 'fa-plus'}`}
                    ></i>
                  </button>
                </div>

                {isAddingTag && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-2 p-2 vt-inset"
                  >
                    <TagTicketAdd onAdd={handleAddTag} />
                  </motion.div>
                )}

                {tags?.length > 0 ? (
                  <TagTicketList items={tags} />
                ) : (
                  !isAddingTag && (
                    <span className="vt-empty">Sin etiquetas</span>
                  )
                )}
              </div>
            </div>

            {/* Tiempos: SLA y cronómetro */}
            <div className="vt-card vt-props">
              <div className="vt-prop-block">
                <SlaCard ticket={ticket} />
              </div>
              <div className="vt-prop-block">
                <TicketTime
                  formatTime={formatTime}
                  running={running}
                  stop={stop}
                  time={time}
                  ticket={ticket}
                  start={start}
                />
              </div>
            </div>
          </div>
        </aside>
      </div>

      <style>{`
        /* Cabecera */
        .vt-header {
          display: flex;
          flex-wrap: wrap;
          align-items: flex-start;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
        }
        .vt-meta {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 6px;
          margin-bottom: 4px;
          font-size: 0.75rem;
          color: var(--gm-text-muted);
        }
        .vt-id {
          font-weight: 600;
          color: var(--gm-text);
        }
        .vt-sep { color: var(--gm-text-faint); }
        .vt-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 1px 8px;
          font-size: 0.7rem;
          font-weight: 600;
          border-radius: 99px;
          color: var(--gm-text);
          background: var(--gm-surface-2);
          border: 1px solid var(--gm-border);
        }
        .vt-chip.is-done {
          color: var(--gm-green);
          background: rgba(34, 197, 94, 0.1);
          border-color: transparent;
        }
        .vt-title {
          margin: 0;
          font-size: 1.2rem;
          font-weight: 600;
          line-height: 1.35;
          color: var(--gm-text);
          overflow-wrap: anywhere;
        }
        .vt-actions {
          display: inline-flex;
          align-items: center;
          gap: 2px;
          padding: 2px;
          background: var(--gm-surface);
          border: 1px solid var(--gm-border);
          border-radius: 10px;
          flex-shrink: 0;
        }
        .vt-actions-sep {
          width: 1px;
          height: 16px;
          margin: 0 2px;
          background: var(--gm-border);
        }

        /* Tarjetas y secciones */
        .vt-card {
          background: var(--gm-surface);
          border: 1px solid var(--gm-border);
          border-radius: 12px;
        }
        .vt-section { padding: 14px 16px; }
        .vt-section.is-divided { border-top: 1px solid var(--gm-border); }
        .vt-section-head {
          display: flex;
          align-items: center;
          gap: 8px;
          min-height: 28px;
        }
        .vt-page .section-subtitle {
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          color: var(--gm-text-muted) !important;
        }
        .vt-page .section-subtitle i { letter-spacing: 0; }
        .vt-description {
          white-space: pre-line;
          font-size: 0.88rem;
          line-height: 1.6;
          color: var(--gm-text);
          overflow-wrap: anywhere;
        }
        .vt-empty {
          margin: 0;
          font-size: 0.78rem;
          color: var(--gm-text-faint);
        }
        .vt-inset {
          background: var(--gm-surface-2);
          border: 1px solid var(--gm-border);
          border-radius: 8px;
        }
        .vt-ai .mb-3:first-child { margin-bottom: 0.5rem !important; }
        .vt-ai p { font-size: 0.84rem; }
        .vt-ai .text-body-secondary.small { color: var(--gm-text-faint) !important; }

        /* Botones */
        .vt-icon-btn {
          width: 30px;
          height: 30px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          font-size: 0.8rem;
          color: var(--gm-text-muted);
          background: transparent;
          border: 1px solid transparent;
          border-radius: 8px;
          transition: background var(--gm-transition), color var(--gm-transition);
        }
        .vt-icon-btn:hover {
          background: var(--gm-surface-2);
          color: var(--gm-text);
        }
        .vt-icon-btn.is-danger:hover {
          color: var(--gm-red);
          background: rgba(239, 68, 68, 0.1);
        }
        .vt-icon-btn.is-sm {
          width: 24px;
          height: 24px;
          font-size: 0.7rem;
        }
        .vt-ghost-btn {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 28px;
          padding: 0 10px;
          font-size: 0.78rem;
          font-weight: 500;
          color: var(--gm-primary);
          background: transparent;
          border: 1px solid var(--gm-border);
          border-radius: 8px;
        }
        .vt-ghost-btn:hover {
          color: var(--gm-primary);
          background: var(--gm-primary-soft);
          border-color: transparent;
        }
        .vt-link { font-size: 0.75rem; }

        /* Conversación (el hilo trae sus propios estilos) */
        .vt-composer { background: var(--gm-surface-2); border-radius: 0 0 12px 12px; }
        .vt-composer .shadow-sm { box-shadow: none !important; }
        .vt-composer .bg-body-tertiary { background: var(--gm-surface) !important; }

        /* Lateral: panel de propiedades */
        .vt-aside {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        @media (min-width: 992px) {
          .vt-aside {
            position: sticky;
            top: 1rem;
            z-index: 10;
          }
        }
        .vt-props > * + * { border-top: 1px solid var(--gm-border); }
        .vt-props > :empty { display: none; }
        .vt-props > :empty + * { border-top: 0; }
        .vt-prop {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          min-height: 42px;
          padding: 7px 14px;
        }
        .vt-prop > :last-child { min-width: 0; }
        .vt-prop-block { padding: 10px 14px; }
        .vt-prop-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 6px;
        }
        .vt-prop-label {
          flex-shrink: 0;
          margin: 0;
          font-size: 0.75rem;
          color: var(--gm-text-muted);
        }
        .vt-prop-value {
          font-size: 0.8rem;
          font-weight: 600;
          color: var(--gm-text);
          text-align: right;
        }
        .vt-prop-select {
          width: auto;
          max-width: 65%;
          font-size: 0.8rem;
          font-weight: 500;
          border-radius: 8px;
          background-color: var(--gm-surface-2);
          border-color: transparent;
        }
        .vt-prop-select:hover { border-color: var(--gm-border); }
        .vt-props .section-subtitle {
          font-size: 0.75rem;
          font-weight: 400;
          letter-spacing: 0;
          text-transform: none;
        }
        .vt-props .tqc-name { font-size: 0.82rem; }
        .vt-person {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 0;
        }
        .vt-avatar {
          width: 24px;
          height: 24px;
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border-radius: 50%;
          font-size: 0.62rem;
          font-weight: 600;
          color: var(--gm-primary);
          background: var(--gm-primary-soft);
        }
        .vt-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        .vt-person-name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 0.8rem;
          font-weight: 500;
          color: var(--gm-text);
        }
        .vt-person.is-empty .vt-avatar {
          color: var(--gm-text-faint);
          background: var(--gm-surface-2);
        }
        .vt-person.is-empty .vt-person-name {
          color: var(--gm-text-muted);
          font-style: italic;
        }

        .vt-running {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          color: var(--gm-green);
          font-weight: 500;
        }
        .vt-running-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--gm-green);
        }
        .pulse-animation { animation: pulse 2s infinite; }
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.5); }
          70% { box-shadow: 0 0 0 6px rgba(34, 197, 94, 0); }
          100% { box-shadow: 0 0 0 0 rgba(34, 197, 94, 0); }
        }

        @media (max-width: 575.98px) {
          .vt-section { padding: 12px; }
          .vt-title { font-size: 1.05rem; }
        }
        @media print {
          .no-print { display: none !important; }
          .col-lg-8, .col-xl-9 {
            width: 100% !important;
            flex: 0 0 100% !important;
            max-width: 100% !important;
          }
        }
      `}</style>
    </div>
  )
}

export default ViewTicket
